/**
 * Banner shown on the client dashboard when a Mexican resident's file has no
 * RFC of their own: a generic one (the SAT's, or the portal's placeholder) or none.
 *
 * Since S-32 this is not only a reminder: the server refuses to start a new
 * contract, sign one, show funding instructions or propose a contribution
 * (409 `rfc_required`, CFF art. 27 A) until the person's own RFC is on file.
 * Money already in the account, withdrawals and statements are untouched, and
 * the banner says so.
 *
 * No CTA yet for self-service update; the copy directs them to their Account
 * Executive. The proper workflow (auto-spawn `obtain_rfc` case, in-portal
 * update flow) is documented as a follow-up.
 */
import { colors } from '../../styles/theme';


interface Props {
  /** True when the dashboard fetched the case data and confirmed
   *  the person's own RFC is not on file. Banner is rendered only
   *  when true; the parent owns the check. */
  show: boolean;
}

export default function RfcReminderBanner({ show }: Readonly<Props>): JSX.Element | null {
  if (!show) return null;
  return (
    <aside
      role="status"
      aria-live="polite"
      className="rounded-2xl p-4 mt-5 flex items-start justify-between gap-4 flex-wrap"
      style={{
        background: colors.warningBg,
        border: `1px solid #FCD34D`,
        color: colors.inkSoft,
      }}
    >
      <div className="min-w-0 flex-1">
        <p
          className="text-xs font-bold uppercase tracking-widest mb-1"
          style={{ color: colors.warning }}
        >
          Pending: your own RFC
        </p>
        <p className="text-sm" style={{ color: colors.ink }}>
          Your profile does not have your own RFC yet. Until you send it to your Account Executive
          you can&apos;t start or sign a contract for a series, or receive the instructions to
          contribute. Your money, withdrawals and statements are not affected.
        </p>
      </div>
    </aside>
  );
}
